
import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogImage?: string;
  structuredData?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogType = 'website',
  ogImage,
  structuredData,
}: SEOProps) => {
  useEffect(() => {
    const siteUrl = 'https://xn--hatayilkyardm-fbc.com.tr';

    // Update title
    if (title) {
      document.title = title;
    }

    // Update meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    if (description) {
      updateMetaTag('description', description);
    }

    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    if (canonical) {
      let linkElement = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!linkElement) {
        linkElement = document.createElement('link');
        linkElement.rel = 'canonical';
        document.head.appendChild(linkElement);
      }
      linkElement.href = canonical === '/' ? siteUrl : `${siteUrl}${canonical}`;
    }

    // Open Graph tags
    if (ogTitle) {
      updateMetaTag('og:title', ogTitle, true);
    }

    if (ogDescription) {
      updateMetaTag('og:description', ogDescription, true);
    }

    if (ogType) {
      updateMetaTag('og:type', ogType, true);
    }

    if (canonical) {
      updateMetaTag('og:url', canonical === '/' ? siteUrl : `${siteUrl}${canonical}`, true);
    }

    if (ogImage) {
      updateMetaTag('og:image', ogImage, true);
    }

    // Twitter tags
    if (ogTitle) {
      updateMetaTag('twitter:title', ogTitle);
    }
    if (ogDescription) {
      updateMetaTag('twitter:description', ogDescription);
    }
    if (ogImage) {
      updateMetaTag('twitter:image', ogImage);
    }

    // Structured Data
    if (structuredData) {
      let scriptElement = document.querySelector('script[type="application/ld+json"][data-page-schema]');
      if (!scriptElement) {
        scriptElement = document.createElement('script');
        scriptElement.setAttribute('type', 'application/ld+json');
        scriptElement.setAttribute('data-page-schema', 'true');
        document.head.appendChild(scriptElement);
      }
      
      // Replace placeholder with actual URL
      const dataString = JSON.stringify(structuredData);
      const processedData = dataString.replace(/VITE_SITE_URL/g, siteUrl);
      scriptElement.textContent = processedData;
    }

    // Update last-modified
    const lastModified = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', lastModified);
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogType, ogImage, structuredData]);
};

export default useSEO;
